import {
  Button,
  CircularProgress,
  Grid,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import React, { Dispatch, SetStateAction } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useToast } from '../../../Hooks/useToast';
import { useRateTalentMutation } from '../../../Redux/api/talentApi';
import { KaziModal } from '../../../Shared Components/Kazi/KaziModal';
import { ProfileType } from '../../../Types/General';
import {
  feedbackField,
  RatingFieldKey,
  ratingFields,
  RatingFormFields,
} from '../../../Types/Talent';
import { jobApi } from '../../../Redux/api/jobApi';
import { useAppDispatch } from '../../../Redux/store';

interface IRateTalentModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  jobInfo: {
    locator: string;
    name: string;
    title: string;
    userType: ProfileType;
    jobType: string;
    avatar: React.ReactNode;
    client_locator: string;
    talent_locator: string;
  };
}

export const RateTalentModal = ({
  open,
  setOpen,
  jobInfo,
}: IRateTalentModalProps) => {
  /* ******************** Variables ******************** */
  const ratingDefaults = Object.fromEntries(
    ratingFields.map(({ key }) => [key, 0]),
  ) as Record<RatingFieldKey, number>;

  const defaultValues: RatingFormFields = {
    ...ratingDefaults,
    [feedbackField.key]: '',
  };
  /* ******************** Hooks ******************** */
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RatingFormFields>({ defaultValues });

  const [rateTalent, { isLoading: submitLoading }] = useRateTalentMutation();
  /* ******************** Functions ******************** */
  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = async (data: RatingFormFields) => {
    try {
      const feedback = data[feedbackField.key];
      // Omit feedback because it was passed above
      const { [feedbackField.key]: excluded, ...scores } = data;

      // Handle the submission logic here
      await rateTalent({
        job_locator: jobInfo.locator,
        client_locator: jobInfo.client_locator,
        talent_locator: jobInfo.talent_locator,
        [feedbackField.key]: feedback,
        scores: { ...scores },
      }).unwrap();
      dispatch(jobApi.util.invalidateTags(['JobsByStatus']));
      handleClose();
      showToast({
        text: 'Rating submitted successfully!',
        variant: 'success',
      });
    } catch (error) {
      showToast({
        text: 'An error occurred while submitting the rating.',
        variant: 'error',
      });
    }
  };
  /* ******************** Effects ******************** */
  /* ******************** JSX ******************** */
  if (!jobInfo) return null;
  return (
    <KaziModal
      title="Rate this talent"
      open={open}
      handleClose={handleClose}
      allowCloseFromHeader={false}
      content={
        <>
          <Grid flex={1} display={'flex'} flexDirection={'row'} gap={2}>
            <Grid
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
            >
              {jobInfo.avatar}
            </Grid>
            <Grid display={'flex'} flexDirection={'column'}>
              <Typography variant="lora-title-large" fontWeight={'bold'}>
                {jobInfo.name}
              </Typography>
              <Typography variant="body-smaller">{jobInfo.title}</Typography>
            </Grid>
          </Grid>
          {ratingFields.map((field, index) => {
            const key = field.key as RatingFieldKey;
            return (
              <React.Fragment key={field.key}>
                {/* {index > 0 && <Divider />} */}
                <Grid display="flex" flexDirection="column" gap={1}>
                  <Typography variant="body-medium" fontWeight="bold">
                    {field.label}
                  </Typography>
                  <Typography variant="body-small" color="text.secondary">
                    {field.description}
                  </Typography>
                  <Controller
                    name={field.key}
                    control={control}
                    rules={{
                      validate: value =>
                        value > 0 ||
                        `${field.label} is required and must be at least 1 star.`,
                    }}
                    render={({ field }) => (
                      <>
                        <Rating
                          value={Number(field.value)}
                          onChange={(_, value) => field.onChange(value)}
                        />
                        {errors[key] && (
                          <Typography variant="body-small" color="error">
                            {errors[key]?.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Grid>
              </React.Fragment>
            );
          })}
          {/* <Divider /> */}
          <Grid display="flex" flexDirection="column" gap={1}>
            <Typography variant="body-medium" fontWeight="bold">
              {feedbackField.label}
            </Typography>
            <Controller
              name={feedbackField.key}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={2}
                  inputProps={{ maxLength: 150 }}
                  placeholder="Write your feedback..."
                />
              )}
            />
          </Grid>
        </>
      }
      actions={[
        <Button onClick={handleClose} variant="outlined">
          Skip
        </Button>,
        <Button
          disabled={submitLoading}
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          endIcon={
            submitLoading ? (
              <CircularProgress color="secondary" size={'18px'} />
            ) : null
          }
        >
          {submitLoading ? 'Submitting...' : 'Submit'}
        </Button>,
      ]}
    />
  );
};
